import { createContext, useContext, type PropsWithChildren } from "react";
import type { CuttlefishConnectionFailure } from "../cuttlefish";
import type { useCuttlefishAdb, useCuttlefishMicrophone } from "../cuttlefish";
import type { EmulatorRow } from "./model/emulators";
import type { Tool } from "./prefs";

export type VehicleUnits = "metric" | "imperial";
export type DockDefault = "bottom" | "right";

/** Row actions each host wires to its own confirm dialogs and navigation. */
export type EmulatorViewActions = {
  restart: (e: EmulatorRow) => void;
  remove: (e: EmulatorRow) => void;
  update: (e: EmulatorRow) => void;
};

/**
 * Everything the emulator view needs from the app embedding it (the portal or
 * the VSCode extension): device connection state, preferences and navigation.
 */
export type EmulatorViewHost = {
  connecting: boolean;
  /** Distinct connection failure class for the health UX. */
  connectionError: CuttlefishConnectionFailure | null;
  adb: ReturnType<typeof useCuttlefishAdb>;
  microphone: ReturnType<typeof useCuttlefishMicrophone>;
  actions: EmulatorViewActions;
  vehicleUnits?: VehicleUnits;
  setVehicleUnits?: (units: VehicleUnits) => void;
  dockDefault?: DockDefault;
  /** Show the tool dock (VHAL, vehicle, logs...); default true. Hosts that open tools elsewhere turn it off. */
  toolDock?: boolean;
  /** Allow detaching the tool dock into a separate browser window. */
  popout: boolean;
  /** Show the scenario run history (otherwise a "coming soon" note). */
  scenarioHistory: boolean;
  /** Header back button; hidden when absent. */
  back?: () => void;
  /** Offer to deploy a snapshot on another pool; hidden when absent. */
  openDeploy?: (snapshot?: string) => void;
  /** Observe each shell command that completed in the Shell tool. */
  onShellResult?: (result: { command: string; output: string; durationMs: number }) => void;
  /** Observe each log line streamed into the Logs tool. */
  onLogLine?: (line: string) => void;
  /** Observe the user switching to a tool (tab click or in-app shortcut). */
  onToolOpened?: (tool: Tool) => void;
  /** Offer "Open in workspace"; hidden when absent. */
  openWorkspace?: (emulatorId: string) => void;
};

const HostContext = createContext<EmulatorViewHost | null>(null);

export function EmulatorViewHostProvider({ value, children }: PropsWithChildren<{ value: EmulatorViewHost }>) {
  return <HostContext.Provider value={value}>{children}</HostContext.Provider>;
}

export function useEmulatorViewHost(): EmulatorViewHost {
  const host = useContext(HostContext);
  if (!host) throw new Error("useEmulatorViewHost must be used inside <EmulatorViewHostProvider>");
  return host;
}
